/**
 * Ask Leon — floating AI chat widget
 *
 * Appears on all pages (rendered in MainLayout).
 * Calls POST /api/assistant/chat with the user's message
 * and a rolling conversation history.
 */

import React, { useState, useRef, useEffect } from 'react';
import api from '../../services/api';
import './AssistantWidget.css';

const BOT_ICON = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73A2 2 0 0 1 10 4a2 2 0 0 1 2-2zm-3 9a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm6 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z" />
  </svg>
);

const SEND_ICON = (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
    <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
  </svg>
);

const CLOSE_ICON = (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

const EXPAND_ICON = (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm14 0a1 1 0 00-1-1h-4a1 1 0 000 2h1.586l-2.293 2.293a1 1 0 001.414 1.414L15 6.414V8a1 1 0 002 0V4zm-14 12a1 1 0 001 1h4a1 1 0 000-2H6.414l2.293-2.293a1 1 0 00-1.414-1.414L5 13.586V12a1 1 0 00-2 0v4zm14 0a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 011.414-1.414L15 13.586V12a1 1 0 012 0v4z" clipRule="evenodd" />
  </svg>
);

const COLLAPSE_ICON = (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M7 10a1 1 0 001 1H5a1 1 0 000 2h3v2a1 1 0 002 0v-3a1 1 0 00-1-1H6a1 1 0 000 2zm6-3a1 1 0 001-1v2a1 1 0 002 0V5a1 1 0 00-1-1h-3a1 1 0 000 2h2a1 1 0 00-1 1zm-7 0a1 1 0 001 1V5a1 1 0 012 0v3a1 1 0 01-1 1H5a1 1 0 010-2zm9 7a1 1 0 00-1 1v-2a1 1 0 00-2 0v3a1 1 0 001 1h3a1 1 0 000-2h-2a1 1 0 001-1z" clipRule="evenodd" />
  </svg>
);

const GREETING = "Hi! I'm Ask Leon. Ask me what requests to submit for your product — for example: \"I have a GA product launching in Qusar, what do I need to submit?\"";

const AssistantWidget = () => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([
    { role: 'assistant', content: GREETING },
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom whenever messages change
  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  // Focus input when widget opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const buildHistory = (msgs) =>
    msgs
      .filter((m) => m.role !== 'assistant' || msgs.indexOf(m) > 0) // skip greeting
      .map((m) => ({ role: m.role, content: m.content }));

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setInput('');
    setLoading(true);
    setError('');

    try {
      const history = buildHistory(messages); // history before the new message
      const response = await api.post('/api/assistant/chat', {
        message: text,
        conversationHistory: history,
      });
      const reply = response.data.reply;
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      const msg =
        err.response?.data?.error || 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([{ role: 'assistant', content: GREETING }]);
    setError('');
  };

  return (
    <>
      {/* Floating toggle button */}
      <button
        className={`assistant-fab ${open ? 'assistant-fab--open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-label="Ask Leon"
        title="Ask Leon"
      >
        {open ? CLOSE_ICON : BOT_ICON}
        {!open && <span className="assistant-fab__label">Ask Leon</span>}
      </button>

      {/* Chat panel */}
      {open && (
        <div className={`assistant-panel ${expanded ? 'assistant-panel--expanded' : ''}`}>
          {/* Header */}
          <div className="assistant-panel__header">
            <div className="assistant-panel__header-left">
              <span className="assistant-panel__bot-icon">{BOT_ICON}</span>
              <div>
                <div className="assistant-panel__title">Ask Leon</div>
                <div className="assistant-panel__subtitle">Powered by Claude AI</div>
              </div>
            </div>
            <div className="assistant-panel__header-actions">
              <button
                className="assistant-panel__clear-btn"
                onClick={clearChat}
                title="Clear conversation"
              >
                Clear
              </button>
              <button
                className="assistant-panel__expand-btn"
                onClick={() => setExpanded((e) => !e)}
                title={expanded ? 'Collapse' : 'Expand'}
                aria-label={expanded ? 'Collapse assistant' : 'Expand assistant'}
              >
                {expanded ? COLLAPSE_ICON : EXPAND_ICON}
              </button>
              <button
                className="assistant-panel__close-btn"
                onClick={() => setOpen(false)}
                aria-label="Close assistant"
              >
                {CLOSE_ICON}
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="assistant-panel__messages">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`assistant-msg assistant-msg--${msg.role}`}
              >
                {msg.role === 'assistant' && (
                  <span className="assistant-msg__avatar">{BOT_ICON}</span>
                )}
                <div className="assistant-msg__bubble">
                  <MessageContent content={msg.content} />
                </div>
              </div>
            ))}

            {loading && (
              <div className="assistant-msg assistant-msg--assistant">
                <span className="assistant-msg__avatar">{BOT_ICON}</span>
                <div className="assistant-msg__bubble assistant-msg__bubble--typing">
                  <span className="assistant-typing-dot" />
                  <span className="assistant-typing-dot" />
                  <span className="assistant-typing-dot" />
                </div>
              </div>
            )}

            {error && (
              <div className="assistant-error">{error}</div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input area */}
          <div className="assistant-panel__input-area">
            <textarea
              ref={inputRef}
              className="assistant-panel__input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about request types, workflows…"
              rows={2}
              disabled={loading}
            />
            <button
              className="assistant-panel__send-btn"
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              aria-label="Send message"
            >
              {SEND_ICON}
            </button>
          </div>
          <div className="assistant-panel__hint">
            Press Enter to send · Shift+Enter for new line
          </div>
        </div>
      )}
    </>
  );
};

/**
 * Renders message content with basic markdown-like formatting:
 * bold (**text**), bullet points, and line breaks.
 */
const MessageContent = ({ content }) => {
  // Split into lines and render with bold support
  const lines = content.split('\n');

  return (
    <div className="assistant-msg__content">
      {lines.map((line, i) => {
        if (line.trim() === '') return null;

        // Render bold segments
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        const rendered = parts.map((part, j) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={j}>{part.slice(2, -2)}</strong>;
          }
          return part;
        });

        // Detect bullet point lines
        if (line.trim().startsWith('- ') || line.trim().startsWith('• ')) {
          const text = line.trim().slice(2);
          const bulletParts = text.split(/(\*\*[^*]+\*\*)/g);
          const bulletRendered = bulletParts.map((part, j) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={j}>{part.slice(2, -2)}</strong>;
            }
            return part;
          });
          return (
            <div key={i} className="assistant-msg__bullet">
              <span className="assistant-msg__bullet-dot">•</span>
              <span>{bulletRendered}</span>
            </div>
          );
        }

        // Arrow lines (→)
        if (line.trim().startsWith('→')) {
          return (
            <div key={i} className="assistant-msg__arrow-line">
              {rendered}
            </div>
          );
        }

        return <div key={i}>{rendered}</div>;
      })}
    </div>
  );
};

export default AssistantWidget;
